/**
 * MediationNeutralView — Phase 7 M3. The cross-firm NEUTRAL's screen.
 *
 * Renders ONLY the allow-listed neutral-view payload (mediationApi.MediationNeutralView):
 * the two sides' disclosed positions + the reconciliation (two endpoints + spread).
 *
 * HARD invariants (contract: 2026-10-11-phase7-mediation-neutral-view-contract.md):
 *   - The two positions are the numbers each attorney DISCLOSED. This component
 *     never computes a figure — it only renders what the backend allow-list sent.
 *   - NO midpoint / average / "fair" marker is ever drawn. The spectrum shows the
 *     two endpoints and the spread (the gap to bridge) — nothing in between.
 *   - It imports NO work-product component (no CarrierPositionLedger, no
 *     MediatorView/brief types, no deliberation/engine types). Pure presentation.
 *
 * Reuses the design vocabulary of MediatorView (CSS tokens, DM Mono tabular
 * financials, orange rail as the one accent) WITHOUT its data path.
 */
import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { FinancialValue } from '@/components/shared/FinancialValue';
import type { MediationNeutralView as NeutralViewData, NeutralViewParty } from '@/services/mediationApi';

const MONO = 'font-mono tabular-nums';

const BASIS_LABEL: Record<string, string> = {
  net_supported_claim: 'Net supported claim',
  policy_limits: 'Policy limits',
  attorney_figure: 'Attorney figure',
  other: 'Other',
};

function MetaChip({ children, title }: { children: React.ReactNode; title?: string }) {
  return (
    <span
      title={title}
      className={`inline-flex items-center ${MONO} uppercase`}
      style={{
        fontSize: 9, letterSpacing: '0.08em', color: 'var(--color-text-secondary)',
        border: '1px solid var(--color-border)', borderRadius: 2, padding: '1px 6px',
      }}
    >
      {children}
    </span>
  );
}

function SectionRule({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-2 mt-6 mb-2">
      <span className={`${MONO} uppercase`} style={{ fontSize: 9, letterSpacing: '0.08em', color: 'var(--color-text-muted)' }}>{text}</span>
      <span className="flex-1" style={{ height: 1, background: 'var(--color-border)' }} />
    </div>
  );
}

// ── One side's disclosed position ────────────────────────────────────────────
function PartyCard({ party }: { party: NeutralViewParty }) {
  return (
    <div
      style={{
        backgroundColor: 'var(--color-bg-elevated)',
        border: '1px solid var(--color-border)',
        borderRadius: 2, padding: 16, flex: 1, minWidth: 0,
      }}
    >
      <div className={`${MONO} uppercase`} style={{ fontSize: 10, letterSpacing: '0.1em', color: 'var(--color-text-muted)' }}>
        {party.side_label}
      </div>
      <div className="mt-0.5" style={{ color: 'var(--color-text-primary)', fontSize: 14 }}>
        {party.represented_party_name || '—'}
      </div>
      {party.disclosed ? (
        <>
          <div className="mt-3">
            <FinancialValue value={party.disclosed_demand} className={`${MONO} text-[28px] leading-none font-medium`} />
          </div>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <MetaChip title="What kind of number this is — not how it was derived">
              {party.disclosed_demand_basis ? (BASIS_LABEL[party.disclosed_demand_basis] ?? party.disclosed_demand_basis) : 'unspecified basis'}
            </MetaChip>
            {party.disclosed_at && (
              <span className={MONO} style={{ fontSize: 9, color: 'var(--color-text-muted)' }}>
                disclosed {new Date(party.disclosed_at).toLocaleDateString('en-US')}
              </span>
            )}
          </div>
          {party.stipulated_facts.length > 0 && (
            <div className="mt-3">
              <div className={`${MONO} uppercase`} style={{ fontSize: 9, letterSpacing: '0.08em', color: 'var(--color-text-muted)' }}>
                Stipulated facts
              </div>
              <ul className="mt-1 space-y-1" style={{ listStyle: 'none', padding: 0 }}>
                {party.stipulated_facts.map((f, i) => (
                  <li key={i} style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>— {f}</li>
                ))}
              </ul>
            </div>
          )}
        </>
      ) : (
        <div className="mt-3" style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>Awaiting disclosure</div>
      )}
    </div>
  );
}

// ── Reconciliation: two endpoints + the spread. NO midpoint marker. ───────────
function ReconciliationBlock({ view }: { view: NeutralViewData }) {
  const r = view.reconciliation;
  const both = r.status === 'both_disclosed' && r.claimant_position != null && r.respondent_position != null;

  return (
    <div
      style={{
        backgroundColor: 'var(--color-bg-elevated)',
        border: '1px solid var(--color-border)',
        borderLeftStyle: 'solid', borderLeftWidth: 3,
        borderLeftColor: both ? 'var(--color-brand)' : 'var(--color-border)',
        borderRadius: 2, padding: 18,
      }}
    >
      <div className={`${MONO} uppercase`} style={{ fontSize: 10, letterSpacing: '0.1em', color: 'var(--color-text-muted)' }}>
        Reconciliation — the gap to bridge
      </div>

      {both ? (
        <>
          {/* two labelled endpoints; the spread is the distance, drawn WITHOUT a
              centre/reference marker (the platform makes no valuation call). */}
          <div className="mt-4 flex items-end justify-between gap-4">
            <div style={{ textAlign: 'left' }}>
              <div className={`${MONO} uppercase`} style={{ fontSize: 9, color: 'var(--color-text-muted)' }}>Claimant</div>
              <FinancialValue value={r.claimant_position} className={`${MONO} text-[22px] leading-none`} />
            </div>
            <div style={{ textAlign: 'center', flex: 1 }}>
              <div className={`${MONO} uppercase`} style={{ fontSize: 9, letterSpacing: '0.1em', color: 'var(--color-brand)' }}>Spread</div>
              <FinancialValue value={r.spread} className={`${MONO} text-[22px] leading-none font-medium`} />
            </div>
            <div style={{ textAlign: 'right' }}>
              <div className={`${MONO} uppercase`} style={{ fontSize: 9, color: 'var(--color-text-muted)' }}>Respondent</div>
              <FinancialValue value={r.respondent_position} className={`${MONO} text-[22px] leading-none`} />
            </div>
          </div>
          {/* a plain rail connecting the two endpoints — markers at the ends only */}
          <div className="mt-3" style={{ position: 'relative', height: 2, background: 'var(--color-border)' }}>
            <span style={{ position: 'absolute', left: 0, top: -3, width: 8, height: 8, borderRadius: 8, background: 'var(--color-text-secondary)' }} />
            <span style={{ position: 'absolute', right: 0, top: -3, width: 8, height: 8, borderRadius: 8, background: 'var(--color-text-secondary)' }} />
          </div>
        </>
      ) : (
        <div className="mt-3" style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
          Awaiting both sides' disclosed positions.
        </div>
      )}

      {r.caveats.length > 0 && (
        <ul className="mt-4 space-y-1" style={{ listStyle: 'none', padding: 0 }}>
          {r.caveats.map((c, i) => (
            <li key={i} style={{ fontSize: 11, color: 'var(--color-text-secondary)', display: 'flex', alignItems: 'flex-start', gap: 6 }}>
              <AlertTriangle size={12} style={{ flexShrink: 0, marginTop: 2 }} aria-hidden /> <span>{c}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function MediationNeutralView({ view }: { view: NeutralViewData }) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <h1 className="font-serif" style={{ fontSize: 24, color: 'var(--color-text-primary)' }}>
          {view.title}
        </h1>
        <MetaChip>{view.status}</MetaChip>
      </div>

      <SectionRule text="Disclosed positions" />
      <div className="flex gap-3 flex-col sm:flex-row">
        {view.parties.map((p, i) => <PartyCard key={i} party={p} />)}
      </div>

      <SectionRule text="Reconciliation" />
      <ReconciliationBlock view={view} />

      {view.excluded.length > 0 && (
        <div className="mt-6" style={{ fontSize: 10, color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
          <div className={`${MONO} uppercase`} style={{ fontSize: 9, letterSpacing: '0.08em', marginBottom: 4 }}>
            This neutral view is a reconciliation, not a valuation. Deliberately not shown:
          </div>
          <ul style={{ listStyle: 'none', padding: 0 }}>
            {view.excluded.map((e, i) => <li key={i}>— {e}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

export default MediationNeutralView;
